/**
 * コーディング面接問題 (/interview-questions/coding) の原稿の読み方と、索引の組み方。
 *
 * 原稿は リポジトリ直下の content/coding-interview/<slug>.md (日本語)。読み込みは coding-interview.server.ts が持つ。
 *
 * 住所の決め (2026-10-24)
 * - 面接問題 (/interview-questions) の下の /interview-questions/coding と /interview-questions/coding/<slug>。
 *   技術面接の問いと同じ入口の下に置き、パンくずも 面接問題 › コーディング面接問題 にする
 * - そのため面接問題の原稿に slug が coding の物は置けない (coding-interview.test.ts が見る)
 * - ほかの言語は /en/interview-questions/coding/<slug> の形で足し、原稿は content/coding-interview/<lang>/<slug>.md
 *
 * 解答は関数で書く。入力の例は 1 行に 1 つの引数を「名前 = 値」で書き、値と出力は JSON の書き方にそろえる。
 * 木を受け取る問題は、引数の名前を root にして、値を段の順の並び (空きは null) で書く。
 * Go の木は TreeNode (Val、Left、Right) で受け取る。
 * 連結リストを受け取る問題は、引数の名前を head (2 本なら head1 と head2) にして、値を並びで書く。輪のある
 * リストは pos (最後の節がつながる先の番号、-1 は輪なし) を足す。節は ListNode (val、next。Go は Val、Next)。
 * 設計の問題は、引数を ops (作るクラスの名前と、呼ぶ操作の名前の並び) と args (それぞれの引数の並び) の 2 つにし、
 * 出力は操作ごとの戻り値の並び (作った所と値を返さない操作は null) にする。pyEntry と jsEntry はクラスの名前、
 * goEntry は作る関数の名前 (newLRUCache など)。Go のメソッドは頭を大文字にする。
 * 入力の値が長いときは、空白で始まる次の行へ続けて書ける (1 行 32 字に収めるため)。
 * tools/problems/verify-examples.sh が、この形を読んで Python と JavaScript と Go で回す
 */
import {
  type MdSection,
  SLUG_PATTERN,
  codeFences,
  parseDate,
  parseFrontMatter,
  parseSections,
  requireFields,
  slugFromPath,
} from "@/lib/md-entry";
import type { ReactNode } from "react";
import { markBreaks } from "@noiman/ui/lg";
import { joinPhrases } from "@/lib/phrase/phrase";

/** 型の並び。索引はこの順で、初学者が上から解ける順にしてある */
export const CODING_TOPICS = [
  { id: "string", label: "文字列" },
  { id: "hash", label: "配列とハッシュ" },
  { id: "two-pointers", label: "ツーポインタ" },
  { id: "sliding-window", label: "スライディングウィンドウ" },
  { id: "stack", label: "スタック" },
  { id: "linked-list", label: "連結リスト" },
  { id: "recursion", label: "再帰" },
  { id: "binary-search", label: "二分探索" },
  { id: "tree", label: "木 (DFS と BFS)" },
  { id: "bst", label: "二分探索木" },
  { id: "heap", label: "ヒープ" },
  { id: "trie", label: "トライ木" },
  { id: "graph", label: "グラフ (グリッドの探索)" },
  { id: "shortest-path", label: "最短経路 (BFS)" },
  { id: "union-find", label: "素集合 (Union-Find)" },
  { id: "topo-sort", label: "トポロジカルソート" },
  { id: "interval", label: "区間" },
  { id: "backtracking", label: "バックトラック" },
  { id: "dp", label: "動的計画法 (DP)" },
  { id: "design", label: "設計" },
] as const;

export type CodingTopicId = (typeof CODING_TOPICS)[number]["id"];

/**
 * 難度。技術面接問題 (/interview-questions) のレベルと同じ 4 段、同じ言葉、同じ順にそろえる (2026-10-30 代表
 * 「これも難度を ジュニア ミドル シニア テックリード で分けたいな」)。技術面接問題は「だれに聞く問いか」、
 * こちらは問題そのものの難しさなので、言葉と順はそろえ、目安の一言だけを替える。
 * 技術面接問題の 4 段 (interview-questions.ts の INTERVIEW_LEVELS) が main に入ったら、id と label はそちらから読む。
 * 棒は 4 本で、ジュニアは 1 本、テックリードは 4 本 (LgLevel の levelSteps が言葉から決める)
 */
export const CODING_LEVELS = [
  { id: "junior", label: "ジュニア", note: "型を 1 つ知っていれば解ける基本の問い。LeetCode の Easy ほど" },
  { id: "middle", label: "ミドル", note: "型を選び、端の場合まで詰めて解く問い。LeetCode の Medium ほど" },
  { id: "senior", label: "シニア", note: "型を組み合わせるか、計算量を一段縮める工夫が要る問い。Medium の難しい物と Hard" },
  { id: "techlead", label: "テックリード", note: "いくつもの操作を持つ仕組みを、データの持ち方から設計して作る問い" },
] as const;

export type CodingLevelId = (typeof CODING_LEVELS)[number]["id"];

/** 詳細ページの節。原稿の ## 見出しはこの順で、この id を {#id} で持つ */
export const CODING_SECTION_IDS = [
  "problem",
  "examples",
  "asked",
  "steps",
  "solution",
  "complexity",
  "mistakes",
  "talk",
] as const;

export type CodingSectionId = (typeof CODING_SECTION_IDS)[number];

export const CODING_LANGUAGES = [
  { id: "python", label: "Python" },
  { id: "javascript", label: "JavaScript" },
  { id: "go", label: "Go" },
] as const;

export type CodingMeta = {
  slug: string;
  title: string;
  order: number;
  topic: CodingTopicId;
  level: CodingLevelId;
  summary: string;
  /** 解答の関数の名前。Python は snake_case、JavaScript と Go は camelCase (Go は小文字で始め、包みの外へ出さない名前) */
  pyEntry: string;
  jsEntry: string;
  goEntry: string;
  verifiedPython: string;
  verifiedNode: string;
  verifiedGo: string;
  verifiedAt: string;
};

export type CodingProblem = CodingMeta & { sections: MdSection<CodingSectionId>[] };

const FIELDS = [
  "slug",
  "title",
  "order",
  "topic",
  "level",
  "summary",
  "pyEntry",
  "jsEntry",
  "goEntry",
  "verifiedPython",
  "verifiedNode",
  "verifiedGo",
  "verifiedAt",
] as const;

const PY_NAME = /^[a-z][a-z0-9_]*$/;
const JS_NAME = /^[a-z][A-Za-z0-9]*$/;
/** 設計の問題の pyEntry と jsEntry (クラスの名前) */
const CLASS_NAME = /^[A-Z][A-Za-z0-9]*$/;
const ARG_LINE = /^[a-z][A-Za-z0-9_]* = .+$/;

function isTopic(value: string): value is CodingTopicId {
  return CODING_TOPICS.some((topic) => topic.id === value);
}

function isLevel(value: string): value is CodingLevelId {
  return CODING_LEVELS.some((level) => level.id === value);
}

/** 例の節は ### 例 N ごとに、入力と出力の 2 つの text の囲みを持つ。入力は 1 行 1 つの「名前 = 値」 */
function checkExamples(section: MdSection, source: string) {
  if (section.items.length < 2) throw new Error(`${source} の例は 2 つ以上置きます`);
  for (const item of section.items) {
    const fences = codeFences(item.body);
    if (fences.length !== 2 || fences.some((fence) => fence.lang !== "text")) {
      throw new Error(`${source} の ${item.heading} は入力と出力の text の囲み 2 つで書きます`);
    }
    for (const line of fences[0].code.trim().split("\n")) {
      /* 空白で始まる行は前の行の値の続き */
      if (/^\s/.test(line)) continue;
      if (!ARG_LINE.test(line)) throw new Error(`${source} の ${item.heading} の入力は「名前 = 値」で書きます (${line})`);
    }
  }
}

/** 解答の節は言語ごとの ### を決まった順で持ち、それぞれにその言語の囲みが 1 つある */
function checkSolutions(section: MdSection, source: string, entries: Record<string, string>) {
  const ids = section.items.map((item) => item.id).join(",");
  const want = CODING_LANGUAGES.map((language) => `solution-${language.id}`).join(",");
  if (ids !== want) throw new Error(`${source} の解答の並びが決まりと違います (${ids})`);
  for (const [index, language] of CODING_LANGUAGES.entries()) {
    const fences = codeFences(section.items[index].body).filter((fence) => fence.lang === language.id);
    if (fences.length !== 1) {
      throw new Error(`${source} の ${language.label} の解答は ${language.id} の囲み 1 つで書きます`);
    }
    const entry = entries[language.id];
    const isClass = CLASS_NAME.test(entry);
    const head = {
      python: isClass ? `class ${entry}` : `def ${entry}(`,
      javascript: isClass ? `class ${entry}` : `function ${entry}(`,
      go: `func ${entry}(`,
    }[language.id];
    if (!fences[0].code.includes(head)) throw new Error(`${source} の ${language.label} の解答に ${head} がありません`);
  }
}

/** 手順は段に分ける。段 1 から順に、3 段以上 */
function checkSteps(section: MdSection, source: string) {
  if (section.items.length < 3) throw new Error(`${source} の手順は 3 段以上に分けます`);
}

export function parseCodingProblem(raw: string, source: string): CodingProblem {
  const { fields, body } = parseFrontMatter(raw, source);
  const f = requireFields(fields, FIELDS, source);
  if (!SLUG_PATTERN.test(f.slug)) throw new Error(`${source} の slug が英小文字とハイフンではありません`);
  if (!isTopic(f.topic)) throw new Error(`${source} の topic ${f.topic} は決まりにありません`);
  if (!isLevel(f.level)) throw new Error(`${source} の level ${f.level} は決まりにありません`);
  const order = Number(f.order);
  if (!Number.isInteger(order) || order < 1) throw new Error(`${source} の order は 1 からの整数で書きます`);
  if (!PY_NAME.test(f.pyEntry) && !CLASS_NAME.test(f.pyEntry)) {
    throw new Error(`${source} の pyEntry は snake_case (設計の問題はクラスの名前) で書きます`);
  }
  if (!JS_NAME.test(f.jsEntry) && !CLASS_NAME.test(f.jsEntry)) {
    throw new Error(`${source} の jsEntry は camelCase (設計の問題はクラスの名前) で書きます`);
  }
  if (!JS_NAME.test(f.goEntry)) throw new Error(`${source} の goEntry は小文字で始まる camelCase で書きます`);

  const sections = parseSections(body, source, CODING_SECTION_IDS);
  checkExamples(sections[1], source);
  checkSteps(sections[3], source);
  checkSolutions(sections[4], source, { python: f.pyEntry, javascript: f.jsEntry, go: f.goEntry });

  return {
    slug: f.slug,
    title: f.title,
    order,
    topic: f.topic,
    level: f.level,
    summary: f.summary,
    pyEntry: f.pyEntry,
    jsEntry: f.jsEntry,
    goEntry: f.goEntry,
    verifiedPython: f.verifiedPython,
    verifiedNode: f.verifiedNode,
    verifiedGo: f.verifiedGo,
    verifiedAt: parseDate(f.verifiedAt, "verifiedAt", source),
    sections,
  };
}

/** 置き場のパスと中身の組から全部を読み、order の順に並べる。slug と order の重なりは投げる */
export function loadCodingProblems(files: Record<string, string>): CodingProblem[] {
  const list: CodingProblem[] = [];
  for (const [path, raw] of Object.entries(files)) {
    const problem = parseCodingProblem(raw, path);
    if (problem.slug !== slugFromPath(path)) {
      throw new Error(`${path} の slug ${problem.slug} がファイル名と違います`);
    }
    if (list.some((other) => other.slug === problem.slug)) throw new Error(`slug ${problem.slug} が 2 本あります`);
    if (list.some((other) => other.order === problem.order)) throw new Error(`order ${problem.order} が 2 本あります`);
    list.push(problem);
  }
  return list.sort((a, b) => a.order - b.order);
}

export function codingMeta(problem: CodingProblem): CodingMeta {
  const { sections: _sections, ...meta } = problem;
  return meta;
}

export const CODING_INDEX_PATH = "/interview-questions/coding";

export function codingPath(slug: string) {
  return `${CODING_INDEX_PATH}/${slug}`;
}

export function codingTopicLabel(id: CodingTopicId) {
  return CODING_TOPICS.find((topic) => topic.id === id)!.label;
}

/** 札と頁の頭に出す難度の言葉 (LgLevel に渡すと棒の数も決まる) */
export function codingLevelLabel(id: CodingLevelId) {
  return CODING_LEVELS.find((level) => level.id === id)!.label;
}

export function codingLevelRank(id: CodingLevelId) {
  return CODING_LEVELS.findIndex((level) => level.id === id);
}

export type CodingIndexGroup = { id: CodingTopicId; label: string; entries: CodingMeta[] };

/**
 * 索引の型ごとの組。順は CODING_TOPICS のまま、中は難度の順 (ジュニアから)、同じ難度は order の順。空の型は出さない。
 * 原稿の order も型の中で難度の順に振ってあり (coding-interview.test.ts が見る)、頁の前後の問題の順と索引の順は同じになる
 */
export function buildCodingIndex(entries: CodingMeta[]): CodingIndexGroup[] {
  return CODING_TOPICS.map((topic) => ({
    id: topic.id,
    label: topic.label,
    entries: entries
      .filter((entry) => entry.topic === topic.id)
      .sort((a, b) => codingLevelRank(a.level) - codingLevelRank(b.level) || a.order - b.order),
  })).filter((group) => group.entries.length > 0);
}

/**
 * 題と要約の折り方。文節の切れ目に幅の無い空白を入れ (joinPhrases)、数とそのあとの日本語 (「1 回」「2 つ」) の
 * あいだは折れない空白にする。置く側は word-break: keep-all を当てる。
 * 切れ目の印 (U+200B) は字に残さず、markBreaks で wbr に替えて描く
 */
export function phraseText(text: string): ReactNode {
  return markBreaks(joinPhrases(text).replace(/([0-9]) \u200b?(?=[^\x00-\x7F])/g, "$1\u00a0"));
}

/**
 * 本文の地の文で、数とそのあとの日本語 (「4 通り」「1 文字」「0 でない」) のあいだを折れない空白にする。
 * 共有の本文の部品が塊にする単位 (回、つ、個など) の外の物 (通り、文字、重、組、倍、万回) も、数と離れない。
 * コードの囲みと行の中のコードは触らない
 */
export function keepNumbersInProse(markdown: string) {
  return markdown
    .split(/(^```[\s\S]*?^```$|`[^`\n]*`)/m)
    .map((part, index) => (index % 2 === 1 ? part : part.replace(/([0-9]) (?=[^\x00-\x7F])/g, "$1\u00a0")))
    .join("");
}
